import { useEffect, useRef, useState } from 'react';
import { Button, type ButtonProps } from './Button';
import { cx } from './cx';
import styles from './button.module.css';

/**
 * The two-click deletion, as one control: the first click *arms* the button — it turns red and
 * its face becomes the confirm word — and only the second click acts. A click anywhere else, an
 * Escape, or focus leaving disarms it, so an armed button never lies in wait.
 *
 * This replaced three one-off confirmations (History's panel, the Playlists page's modal, the
 * playlist editor's inline pair) because a reader should meet *one* deletion grammar, not three.
 * The word itself comes from the caller (`confirmLabel`) — `ui/` stays word-free, `Modal`'s
 * `closeLabel` precedent.
 *
 * The wrapper `<span>` is `display: contents`, so it adds no box to whatever layout the button sits
 * in — it exists only so the outside-click test has something to ask `contains()` about, since
 * `Button` forwards no ref. Disarm-on-pointerdown must NOT fire for the button itself: `pointerdown`
 * precedes `click`, and a self-match would disarm and then re-arm on every second click, making the
 * confirmation unreachable.
 */
export interface ConfirmButtonProps extends Omit<ButtonProps, 'onClick'> {
  /** The armed face — "Confirm", from the caller's catalogue. */
  readonly confirmLabel: string;
  /** The armed state's hover title: what exactly the second click destroys, where words help. */
  readonly confirmTitle?: string;
  readonly onConfirm: () => void;
}

export function ConfirmButton({
  confirmLabel,
  confirmTitle,
  onConfirm,
  children,
  icon,
  label,
  title,
  variant,
  disabled,
  className,
  ...rest
}: ConfirmButtonProps) {
  const [armed, setArmed] = useState(false);
  const root = useRef<HTMLSpanElement | null>(null);

  /** The `<button>` itself — the wrapper is `display: contents`, so its one child is the control. */
  const buttonOf = (): HTMLElement | null => {
    const element = root.current?.firstElementChild;
    return element instanceof HTMLElement ? element : null;
  };

  // The armed face must never *shrink* the button (2026-08-02): a short confirm word under a
  // pointer that just pressed a longer face moves the second click's target. Measured at the arming
  // press and pinned as a `min-width` — imperatively, because `Button` deliberately has no style
  // prop; growing is still allowed, only shrinking is not. Cleared on every way out of the armed
  // state, which all funnel through `armed`.
  useEffect(() => {
    if (armed) return;
    const button = buttonOf();
    if (button !== null) button.style.minWidth = '';
  }, [armed]);

  useEffect(() => {
    if (!armed) return;

    // `pointerdown` rather than `click`, the Menu's reasoning: a click on a scrollbar never fires
    // `click`, and an armed delete button surviving a scroll is exactly the lying-in-wait above.
    const onPointerDown = (event: PointerEvent): void => {
      if (root.current?.contains(event.target as Node) === true) return;
      setArmed(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setArmed(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [armed]);

  // A button that became disabled mid-arm (the action started elsewhere) must not stay red —
  // adjusted during render (React's documented pattern), not in an effect, so no extra paint
  // ever shows a disabled button still wearing the confirm face.
  if (disabled === true && armed) setArmed(false);

  return (
    <span
      style={{ display: 'contents' }}
      ref={root}
      onBlur={(event) => {
        // Keyboard users leave by Tab, not by pointerdown — focus moving outside disarms too.
        if (root.current?.contains(event.relatedTarget as Node) !== true) setArmed(false);
      }}
    >
      <Button
        {...rest}
        variant={armed ? 'danger' : variant}
        // The armed face recolours the button without reshaping it — a pill must stay a pill.
        className={cx(className, armed && variant === 'pill' && styles.dangerPill)}
        // An icon-only remove gains the visible word while armed — adding a Confirm label.
        icon={armed ? false : icon}
        label={armed ? confirmLabel : label}
        title={armed ? (confirmTitle ?? confirmLabel) : title}
        disabled={disabled}
        onClick={() => {
          if (armed) {
            setArmed(false);
            onConfirm();
          } else {
            const button = buttonOf();
            if (button !== null) {
              button.style.minWidth = `${String(Math.ceil(button.getBoundingClientRect().width))}px`;
            }
            setArmed(true);
          }
        }}
      >
        {armed ? (
          <>
            {/* An armed icon button keeps its glyph beside the word: the face gains the confirm
                label without changing what the button is a picture of. */}
            {icon === true ? children : null}
            {confirmLabel}
          </>
        ) : (
          children
        )}
      </Button>
    </span>
  );
}
